import '../styles.css';
import Button from '../components/Button';
import PageHeader from '../components/PageHeader';
import { ResumeIcon } from '../icons/navigationIcons';
import { storageAssets } from '../storage_assets';

/* Typography: see styles.css — .text-header-main, .text-body, .text-body-sm, .text-inline-code */

const openResume = () => {
    const link = document.createElement('a');
    link.href = '/resume.pdf';
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

const AboutPage = () => {
    return (
        <div className="scrollable-window scrollable-window--wide">
            <div style={styles.wholeThingBox}>
                <div style={styles.boxMain}>
                    <PageHeader
                        title="My story"
                        action={
                            <Button
                                variant="text"
                                text="Resume"
                                icon={<ResumeIcon />}
                                iconPosition="end"
                                onClick={openResume}
                            />
                        }
                    />
                    <div className="text-body-sm">
                        I've been drawn to design for as long as I can remember. When I was very young, I visited my aunt's modern
                        home. Its unique design inspired me and I started blueprinting my own home designs—trying to balance how things looked with how they worked.
                        Years later, I started college intending to study music, but after my first year, I discovered design and
                        fell in love with that same harmony of form and purpose.
                    </div>
                    <div className="text-body-sm">
                        In college I joined a startup incubator called{' '}
                        <a href="https://sandbox.ing/" target="_blank" rel="noopener noreferrer" style={styles.inlineLink}>
                            Sandbox
                        </a>{' '}
                        (SB04) because I wanted to design things that would have real world
                        impact. In Sandbox I noticed a pattern amongst people in my cohort: many designers would hand off polished mockups, and the final product
                        rarely matched. I didn't want that gap in my own work, so I taught myself to code.
                    </div>
                    <div className="text-body-sm">
                        I went on to co-found{' '}
                        <a href="https://maddergames.com/about" target="_blank" rel="noopener noreferrer" style={styles.inlineLink}>
                            Madder
                        </a>
                        , designing and shipping a mobile controller app (2,000+ downloads), multiple games, and a cross-platform
                        console experience. I also built{' '}
                        <a href="https://saysoenglish.vercel.app" target="_blank" rel="noopener noreferrer" style={styles.inlineLink}>
                            Sayso
                        </a>{' '}
                        to help Spanish-speaking immigrants learn practical, everyday English. At the same time I worked as a graphic designer for BYU athletics at a very pivotal time in which I participated in designing motion graphics for elite athletes. I took many of my graphics skills from that job into my startup work—using UI motion (Lottie, Rive, SVG) to create motivational experiences.
                    </div>
                    <div className="text-body-sm">
                        Later, at{' '}
                        <a href="https://www.askelephant.ai/" target="_blank" rel="noopener noreferrer" style={styles.inlineLink}>
                            AskElephant
                        </a>
                        , I pushed further—becoming the first non-dev to gain repo access and using AI to design directly in code
                        instead of handing off mockups. Now, I use code editors as a large part of my design process—from prototyping interfaces to managing agents and product
                        context. This has empowered me to achieve a high level of quality in my designs while doing so faster than ever.
                    </div>
                    <img src={storageAssets.adamPhoto} alt="Adam" style={styles.image} />
                    <div className="text-body-sm">
                        Outside of design I still imagine{' '}
                        <a
                            href="https://medium.com/@aoshumway8/tiny-ish-houses-58324259ed47"
                            target="_blank"
                            rel="noopener noreferrer"
                            style={styles.inlineLink}
                        >
                            unique living experiences. 
                        </a>{' '}
                        I also enjoy playing classical music on the piano and the guitar. I
                        love to ski, camp, cook, speak Spanish, and spend time with my wife and son.{' '} Occasionally, I am kept up at night thinking about{' '}
                        <a
                            href="https://en.wikipedia.org/wiki/Time_dilation"
                            target="_blank"
                            rel="noopener noreferrer"
                            style={styles.inlineLink}
                        >
                            Einstein's theory of relativity.
                        </a>
                    </div>
                </div>
            </div>
        </div>
    );
};

const styles = {
    wholeThingBox: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '40px',
        alignSelf: 'stretch',
    },
    boxMain: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: '20px',
        width: 'var(--content-width)',
        padding: '0 clamp(1rem, 4vw, 2rem)',
        boxSizing: 'border-box',
        margin: '0',
        position: 'relative',
        zIndex: 1,
    },
    image: {
        alignSelf: 'center',
        width: 'min(100%, 420px)',
        height: 'auto',
        maxWidth: '100%',
    },
    deviderStroke: {
        height: '1px',
        backgroundColor: '#9B9B9B',
        alignSelf: 'stretch',
    },
    inlineLink: {
        color: 'var(--color-accent-text)',
        textDecoration: 'underline',
        textUnderlineOffset: '2px',
        cursor: 'pointer',
    },
};

export default AboutPage;
